import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  Spinner,
  Banner,
  Button,
} from '@shopify/polaris'

export default function BillingCallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')

  const billingStatus = searchParams.get('billing')
  const storeId = searchParams.get('store')
  const errorMessage = searchParams.get('error')

  useEffect(() => {
    if (billingStatus === 'success') {
      setStatus('success')
      // Redirect to dashboard after 3 seconds
      const timer = setTimeout(() => {
        navigate(`/dashboard?store=${storeId}`)
      }, 3000)
      return () => clearTimeout(timer)
    } else if (billingStatus === 'declined' || errorMessage) {
      setStatus('error')
    }
  }, [billingStatus, errorMessage, navigate, storeId])

  const handleContinue = () => {
    if (status === 'success') {
      navigate(`/dashboard?store=${storeId}`)
    } else {
      navigate(`/billing/select-plan?store=${storeId}&error=declined`)
    }
  }

  if (status === 'loading') {
    return (
      <Page title="Processing Subscription">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' }}>
                <Spinner size="large" />
                <div style={{ marginTop: '24px' }}>
                  <Text as="p" variant="bodyLg" alignment="center">
                    Processing your subscription...
                  </Text>
                </div>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  if (status === 'error') {
    return (
      <Page title="Subscription Declined">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>
                {errorMessage || 'Your subscription was declined. Please try again or select a different plan.'}
              </p>
            </Banner>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingLg">
                  What would you like to do?
                </Text>

                <Text as="p" variant="bodyMd">
                  To use the WhatsApp CRM app, you'll need an active subscription.
                  You can try selecting a different plan or contact support if you're experiencing issues.
                </Text>

                <div>
                  <Button variant="primary" onClick={handleContinue}>
                    Choose a Different Plan
                  </Button>
                </div>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  // Success state
  return (
    <Page title="Welcome to WhatsApp CRM!">
      <Layout>
        <Layout.Section>
          <Banner tone="success">
            <p>Your subscription has been activated successfully!</p>
          </Banner>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="500">
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                    margin: '0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              </div>

              <BlockStack gap="300">
                <Text as="h2" variant="headingLg" alignment="center">
                  Subscription Activated!
                </Text>

                <Text as="p" variant="bodyMd" alignment="center" tone="subdued">
                  Your 14-day free trial has started. You have full access to all features.
                </Text>
              </BlockStack>

              <BlockStack gap="300">
                <Text as="h3" variant="headingMd">
                  What's next?
                </Text>

                <BlockStack gap="200">
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div
                      style={{
                        minWidth: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#E3F2FD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#1976D2',
                      }}
                    >
                      1
                    </div>
                    <div>
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        Connect your WhatsApp Business account
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Head to Settings to configure your WhatsApp Business API
                      </Text>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div
                      style={{
                        minWidth: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#E3F2FD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#1976D2',
                      }}
                    >
                      2
                    </div>
                    <div>
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        Sync your store data
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Import your customers and orders from Shopify
                      </Text>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div
                      style={{
                        minWidth: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#E3F2FD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#1976D2',
                      }}
                    >
                      3
                    </div>
                    <div>
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        Set up automated messages
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Create automated workflows for cart abandonment, order updates, and more
                      </Text>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div
                      style={{
                        minWidth: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#E3F2FD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#1976D2',
                      }}
                    >
                      4
                    </div>
                    <div>
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        Start engaging with customers
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Send your first WhatsApp message and watch your engagement soar!
                      </Text>
                    </div>
                  </div>
                </BlockStack>
              </BlockStack>

              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <Text as="p" variant="bodySm" tone="subdued">
                  Redirecting to dashboard in a few seconds...
                </Text>
                <div style={{ marginTop: '16px' }}>
                  <Button variant="primary" onClick={handleContinue}>
                    Go to Dashboard Now
                  </Button>
                </div>
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}
